import { Box, IconButton, Typography } from "@mui/material";
import { Close, HighlightAlt } from "@mui/icons-material";
import { PortalTooltip } from "../PortalTooltip";
import type { JobMapProps, JobMapViewModel } from "./JobMap.types";

interface JobMapToolbarProps {
	vm: JobMapViewModel;
	props: JobMapProps;
}

/** Toolbar for the JobMap — job count, selection info, lasso toggle, close. */
export function JobMapToolbar({ vm, props }: JobMapToolbarProps) {
	const hasSelectedFeatures = vm.effectiveSelectedFeatureIds.size > 0;

	const getSelectionText = () => {
		if (vm.effectiveSelectedFeatureIds.size === 0) return null;
		let polygons = 0;
		let points = 0;
		vm.effectiveSelectedFeatureIds.forEach((id) => {
			if (id.startsWith("points-")) {
				points++;
			} else {
				polygons++;
			}
		});
		const parts = [];
		if (polygons > 0) parts.push(`${polygons} polygon${polygons !== 1 ? "s" : ""}`);
		if (points > 0) parts.push(`${points} point${points !== 1 ? "s" : ""}`);
		return `Selected: ${parts.join(" and ")}`;
	};

	const getSelectButtonTooltip = () => {
		if (hasSelectedFeatures) return "Clear selection";
		if (vm.effectiveLassoMode) return "Click to add points, click first point to close";
		return "Select features";
	};

	return (
		<Box
			className="map-toolbar"
			sx={{
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				padding: 1.5,
				borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
				flexShrink: 0,
			}}
		>
			<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
				<Typography variant="h6">
					{vm.effectiveJobs.length} {vm.effectiveJobs.length === 1 ? "Job" : "Jobs"}
				</Typography>
				{getSelectionText() && (
					<Typography variant="body2" color="error.main">
						{getSelectionText()}
					</Typography>
				)}
			</Box>
			<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
				{props.toolbarActions}
				<PortalTooltip title={getSelectButtonTooltip()}>
					<IconButton
						onClick={vm.onToggleLasso}
						aria-label="select features"
						size="small"
						sx={{
							color: hasSelectedFeatures ? "error.main" : vm.effectiveLassoMode ? "#2196f3" : undefined,
						}}
					>
						<HighlightAlt />
					</IconButton>
				</PortalTooltip>
				<IconButton onClick={props.onClose ?? (() => {})} aria-label="close map" size="small">
					<Close />
				</IconButton>
			</Box>
		</Box>
	);
}
